import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { BiArrowBack } from 'react-icons/bi';
import { useLocation, useNavigate } from 'react-router-dom';
import { platformAPI } from '../../services/platformAPI';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { settingsBase } from './settings/settingsPaths';
import './tender/CustomerMaster.scss';
import './tender/PersonnelPage.scss';
import './settings/EmailSettingsPage.scss';

const FALLBACK_PROVIDERS = [
  {
    value: 'gemini',
    label: 'Google Gemini',
    defaultModel: 'gemini-2.5-flash',
    models: [
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' }
    ]
  },
  {
    value: 'openai',
    label: 'OpenAI',
    defaultModel: 'gpt-4o-mini',
    models: [
      { value: 'gpt-4o-mini', label: 'GPT-4o mini' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
      { value: 'gpt-4.1', label: 'GPT-4.1' }
    ]
  }
];

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
}

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const PlatformSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = settingsBase(location.pathname);
  const [denied, setDenied] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [providers, setProviders] = useState(FALLBACK_PROVIDERS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [provider, setProvider] = useState('gemini');
  const [modelPreset, setModelPreset] = useState('gemini-2.5-flash');
  const [customModel, setCustomModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [usageNotes, setUsageNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useDocumentTitle('AI');

  const applyPayload = useCallback((payload) => {
    const c = payload?.config || null;
    if (payload?.providers?.length) setProviders(payload.providers);
    setCfg(c);
    if (!c) return;
    const list = payload?.providers?.length ? payload.providers : FALLBACK_PROVIDERS;
    const nextProvider = list.some((item) => item.value === c.provider) ? c.provider : 'gemini';
    const meta = list.find((item) => item.value === nextProvider) || list[0];
    const model = c.model || meta?.defaultModel || '';
    setProvider(nextProvider);
    if (meta?.models?.some((item) => item.value === model)) {
      setModelPreset(model);
      setCustomModel('');
    } else {
      setModelPreset('custom');
      setCustomModel(model);
    }
    setEnabled(Boolean(c.enabled));
    setUsageNotes(c.usageNotes || '');
    setApiKey('');
  }, []);

  const loadConfig = useCallback(() => {
    setLoadError('');
    setLoading(true);
    return platformAPI
      .getAiConfig()
      .then((response) => applyPayload(response.data?.data))
      .catch((error) => {
        if (error.response?.status === 403) setDenied(true);
        else setLoadError(error.response?.data?.message || 'Unable to load AI configuration');
      })
      .finally(() => setLoading(false));
  }, [applyPayload]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const meta = providers.find((item) => item.value === provider) || providers[0];
  const resolvedModel = modelPreset === 'custom' ? customModel.trim() : modelPreset;
  const providerChanged = Boolean(cfg?.apiKeyConfigured && cfg.provider && cfg.provider !== provider);

  const save = async (event) => {
    event.preventDefault();
    setFeedback(null);
    if (!resolvedModel) {
      setFeedback({ variant: 'danger', text: 'Choose or enter a model.' });
      return;
    }
    if (providerChanged && !apiKey.trim()) {
      setFeedback({
        variant: 'danger',
        text: 'Enter the API key for this provider. The saved key stays hidden and is not reused.'
      });
      return;
    }
    if (enabled && !cfg?.apiKeyConfigured && !apiKey.trim()) {
      setFeedback({ variant: 'danger', text: 'Add an API key before enabling AI.' });
      return;
    }
    setSaving(true);
    try {
      const response = await platformAPI.updateAiConfig({
        provider,
        model: resolvedModel,
        apiKey: apiKey.trim() || undefined,
        enabled,
        usageNotes
      });
      applyPayload(response.data?.data);
      setFeedback({
        variant: 'success',
        text: 'AI configuration saved. The key is encrypted and will only be shown as XXXXXXXXXXXX.'
      });
    } catch (error) {
      setFeedback({ variant: 'danger', text: error.response?.data?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setFeedback(null);
    setTesting(true);
    try {
      const response = await platformAPI.testAiConfig({
        provider,
        model: resolvedModel || undefined,
        apiKey: apiKey.trim() || undefined
      });
      const tested = response.data?.data;
      await loadConfig();
      const label = providers.find((item) => item.value === tested?.provider)?.label || 'The provider';
      setFeedback({ variant: 'success', text: `${label} accepted the key for ${tested?.model || resolvedModel}.` });
    } catch (error) {
      await loadConfig().catch(() => {});
      setFeedback({ variant: 'danger', text: error.response?.data?.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const clearStoredKey = async () => {
    if (!window.confirm('Remove the stored API key? AI features stop until a new key is saved.')) return;
    setFeedback(null);
    setSaving(true);
    try {
      const response = await platformAPI.updateAiConfig({ clearApiKey: true, enabled: false, provider, model: resolvedModel });
      applyPayload(response.data?.data);
      setFeedback({ variant: 'success', text: 'Stored API key removed.' });
    } catch (error) {
      setFeedback({ variant: 'danger', text: error.response?.data?.message || 'Could not remove the key' });
    } finally {
      setSaving(false);
    }
  };

  if (denied) {
    return <Alert variant="warning">A workspace administrator maintains the AI provider.</Alert>;
  }

  if (loading) {
    return (
      <div className="py-5 text-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading AI settings…
      </div>
    );
  }

  const active = Boolean(cfg?.enabled && cfg?.apiKeyConfigured);
  const testLabel = cfg?.lastTestStatus === 'success' ? 'Last test passed' : cfg?.lastTestStatus === 'failed' ? 'Last test failed' : 'Not tested yet';

  return (
    <div className="cust-page">
      <form className="cust-record pers-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(base)}>
              <BiArrowBack />
              Settings
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>AI</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">Settings</p>
                <h1>AI</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{active ? 'In use' : 'Not enabled'}</span>
                  <span className="cust-chip">{cfg?.apiKeyConfigured ? 'Key stored' : 'No key yet'}</span>
                  <span className="cust-chip">{testLabel}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            <Button size="sm" variant="primary" type="submit" disabled={saving || testing}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </header>

        {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
        {feedback ? (
          <Alert variant={feedback.variant} dismissible onClose={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        ) : null}
        {cfg && !cfg.encryptionReady ? (
          <Alert variant="warning">
            The server needs <code>AI_ENCRYPTION_KEY</code> before a key can be stored. Model choices can still be saved.
          </Alert>
        ) : null}

        <section className="cust-section">
          <div className="cust-section__head">
            <span>01</span>
            <div>
              <h2>Provider</h2>
              <p>Document questions, technical drafts, eligibility notes, library wording, and document summaries use this provider. Saving Gemini uses Gemini. Saving OpenAI uses OpenAI. Each of those screens stays quiet until a key is saved and the provider is switched on.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Field label="Use AI" wide={12}>
              <Form.Check
                type="switch"
                id="ai-enabled"
                label={enabled ? 'The workspace may call this provider' : 'AI calls are switched off'}
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            </Field>
            <Field label="Provider" wide={6}>
              <Form.Select
                value={provider}
                onChange={(event) => {
                  const next = event.target.value;
                  const nextMeta = providers.find((item) => item.value === next);
                  setProvider(next);
                  setModelPreset(nextMeta?.defaultModel || 'custom');
                  setCustomModel('');
                }}
              >
                {providers.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
            </Field>
            <Field label="Model" wide={6}>
              <Form.Select value={modelPreset} onChange={(event) => setModelPreset(event.target.value)}>
                {(meta?.models || []).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
                <option value="custom">Other model ID</option>
              </Form.Select>
            </Field>
            {modelPreset === 'custom' ? (
              <Field label="Model ID" wide={6}>
                <Form.Control
                  value={customModel}
                  onChange={(event) => setCustomModel(event.target.value)}
                  placeholder={provider === 'gemini' ? 'gemini-2.5-flash' : 'gpt-4o-mini'}
                  autoComplete="off"
                />
              </Field>
            ) : null}
          </div>
        </section>

        <section className="cust-section">
          <div className="cust-section__head">
            <span>02</span>
            <div>
              <h2>API key</h2>
              <p>The key is encrypted before it is stored. After save it is never sent back to the browser.</p>
            </div>
          </div>
          <div className="cust-fields">
            {cfg?.apiKeyConfigured ? (
              <Field label="Saved key" wide={6}>
                <Form.Control value={cfg.apiKeyMasked || 'XXXXXXXXXXXX'} readOnly tabIndex={-1} aria-label="Saved API key, masked" />
              </Field>
            ) : null}
            <Field label={cfg?.apiKeyConfigured ? 'Replace key' : 'API key'} wide={6}>
              <Form.Control
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                placeholder={cfg?.apiKeyConfigured ? 'Leave blank to keep the saved key' : 'Paste the provider key'}
              />
            </Field>
            <Field label="Notes" wide={12}>
              <Form.Control
                as="textarea"
                rows={2}
                value={usageNotes}
                onChange={(event) => setUsageNotes(event.target.value)}
                placeholder="Billing owner or rotation date. Do not paste the key here."
              />
            </Field>
            {(cfg?.lastTestedAt || cfg?.lastError || cfg?.updatedAt) ? (
              <Field label="Status" wide={12}>
                <p className="text-muted mb-0">
                  {cfg?.updatedAt ? `Saved ${formatWhen(cfg.updatedAt)}. ` : ''}
                  {cfg?.lastTestedAt ? `Tested ${formatWhen(cfg.lastTestedAt)}. ` : ''}
                  {cfg?.lastTestStatus === 'failed' && cfg.lastError ? cfg.lastError : ''}
                </p>
              </Field>
            ) : null}
          </div>
        </section>

        <section className="cust-section">
          <div className="cust-section__head">
            <span>03</span>
            <div>
              <h2>Try it</h2>
              <p>Save first when you want the test to use the stored key. A new key in the box is tested without being shown again.</p>
            </div>
          </div>
          <div className="cust-fields">
            <div className="cust-field cust-field--12 mail-try__actions">
              <Button size="sm" variant="outline-primary" type="button" onClick={testConnection} disabled={testing || saving || (!apiKey.trim() && !cfg?.apiKeyConfigured)}>
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              {cfg?.apiKeyConfigured ? (
                <Button size="sm" variant="outline-danger" type="button" onClick={clearStoredKey} disabled={saving || testing}>
                  Remove stored key
                </Button>
              ) : null}
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

export default PlatformSettingsPage;
